import { createTheme, PaletteMode } from '@mui/material/styles';
import { goldenSpacing, LINE_HEIGHT, TYPE_SCALE } from './styles/golden';

const { breakpoints } = createTheme();
const desktop = breakpoints.up('md');
const phone = breakpoints.down('sm');

// Headings step by powers of φ and scale down one step on small screens
const heading = (mobileRem: number, desktopRem: number, fontWeight = 700) => ({
  fontWeight,
  fontSize: `${mobileRem}rem`,
  lineHeight: LINE_HEIGHT.heading,
  letterSpacing: '-0.01em',
  [desktop]: { fontSize: `${desktopRem}rem` },
});

// Viviplu brand values for each mode (mirrors the CSS variables in index.css)
const BRAND_MODES = {
  light: {
    bg: '#FFFFFF', surface: '#FFFFFF', raised: '#FFFFFF', line: '#E7ECE5',
    ink: '#101E1B', muted: '#46514F', faint: '#606C69',
    accent: '#007B6B', accentStrong: '#164A41', onAccent: '#FFFFFF',
  },
  dark: {
    bg: '#0E1816', surface: '#152320', raised: '#1C2D29', line: '#243430',
    ink: '#F2EEEF', muted: '#ABBCB7', faint: '#869692',
    accent: '#42CAB4', accentStrong: '#2FBDA7', onAccent: '#101E1B',
  },
};

export const createAppTheme = (mode: PaletteMode) => {
  const brand = BRAND_MODES[mode];

  return createTheme({
    spacing: goldenSpacing,
    palette: {
      mode,
      primary: {
        main: '#EA5480',
        light: '#FF85A9',
        dark: '#D03E6A',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: brand.accent,
        dark: brand.accentStrong,
        contrastText: brand.onAccent,
      },
      success: {
        main: brand.accent,
        contrastText: brand.onAccent,
      },
      error: {
        main: mode === 'light' ? '#C62828' : '#FF8A80',
      },
      warning: {
        main: '#ED9A00',
      },
      info: {
        main: brand.accent,
      },
      background: {
        default: brand.bg,
        paper: brand.raised,
      },
      text: {
        primary: brand.ink,
        secondary: brand.muted,
        disabled: brand.faint,
      },
      divider: brand.line,
    },
    typography: {
      fontFamily: 'Roboto, Arial, sans-serif',
      h1: heading(TYPE_SCALE.title, TYPE_SCALE.display),
      h2: heading(TYPE_SCALE.heading, TYPE_SCALE.title),
      h3: heading(TYPE_SCALE.heading, TYPE_SCALE.title),
      h4: heading(TYPE_SCALE.headingCompact, TYPE_SCALE.heading),
      h5: heading(TYPE_SCALE.lead, TYPE_SCALE.subheading, 600),
      h6: heading(TYPE_SCALE.lead, TYPE_SCALE.lead, 600),
      subtitle1: { lineHeight: LINE_HEIGHT.body },
      body1: { lineHeight: LINE_HEIGHT.body },
      body2: { lineHeight: 1.5 },
      caption: { fontSize: `${TYPE_SCALE.small}rem` },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: 'var(--joan-bg)', color: 'var(--joan-ink)' },
          // Browsers repaint autofilled fields (blue in Chrome); delaying that transition keeps
          // the field's own background, and the text stays in the brand ink colour
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, select:-webkit-autofill': {
            WebkitTextFillColor: 'var(--joan-ink)',
            caretColor: 'var(--joan-ink)',
            transition: 'background-color 600000s 0s, color 600000s 0s',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            minHeight: 44, // comfortable touch target
          },
          sizeSmall: {
            minHeight: 34,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { padding: 10 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 13,
            boxShadow: 'var(--joan-shadow)',
          },
        },
      },
      // Fields: neutral outlines from the brand lines, ink when active
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--joan-line-strong)' },
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--joan-ink)' },
            '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--joan-ink)' },
          },
          // MUI paints autofilled fields blue (#266798) in dark mode; keep the field's own look instead
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: 'none',
              WebkitTextFillColor: 'var(--joan-ink)',
              caretColor: 'var(--joan-ink)',
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          input: {
            '&:-webkit-autofill': {
              WebkitBoxShadow: 'none',
              WebkitTextFillColor: 'var(--joan-ink)',
              caretColor: 'var(--joan-ink)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: 'var(--joan-muted)',
            '&.Mui-focused:not(.Mui-error)': { color: 'var(--joan-ink)' },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: { color: 'var(--joan-muted)' },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: { color: 'var(--joan-muted)' },
        },
      },
      MuiDialog: {
        defaultProps: {
          fullWidth: true,
        },
        styleOverrides: {
          paper: {
            borderRadius: 13,
            backgroundColor: 'var(--joan-dialog)',
            backgroundImage: 'none',
            border: '1px solid var(--joan-line)',
            [phone]: {
              '&:not(.MuiDialog-paperFullScreen)': {
                margin: 13,
                width: 'calc(100% - 26px)',
                maxHeight: 'calc(100% - 26px)',
              },
            },
          },
          paperFullScreen: {
            borderRadius: 0,
            border: 'none',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            flexWrap: 'wrap',
            gap: 8,
            '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
    },
  });
};

export default createAppTheme('light');
